import { useCallback, useRef, useState } from 'react';
import type { CanvasMode, Diagram, ExcalidrawData, LastRequest, PendingImage, PipelineStage } from '../store/types.ts';
import { STAGE_LABELS } from '../store/types.ts';
import { generationApi } from '../lib/api-client.ts';
import { renderMermaidToSvg } from '../lib/mermaid-config.ts';

type GenerationHandlers = {
  onCommitCode: (diagramId: string, code: string, source: 'chat' | 'autofix', note: string) => void;
  onAppendChat: (diagramId: string, role: 'user' | 'assistant' | 'error', text: string) => void;
  onExcalidrawGenerated: (data: ExcalidrawData) => void;
  onPipelineChange: (stage: PipelineStage) => void;
  onStatusChange: (text: string) => void;
};

export function useGeneration(
  workspaceId: string | undefined,
  canvasMode: CanvasMode,
  selectedDiagram: Diagram | undefined,
  handlers: GenerationHandlers,
) {
  const [isSending, setIsSending] = useState(false);
  const [lastRequest, setLastRequest] = useState<LastRequest | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const startPipeline = useCallback(
    (stage: PipelineStage, statusOverride?: string) => {
      handlers.onPipelineChange(stage);
      handlers.onStatusChange(statusOverride || STAGE_LABELS[stage]);
    },
    [handlers],
  );

  const stopRequest = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const runGeneration = useCallback(
    async (request: LastRequest, mode: 'generate' | 'fix-only') => {
      if (!selectedDiagram || !workspaceId) return;

      const controller = new AbortController();
      abortRef.current = controller;
      setIsSending(true);

      try {
        startPipeline('sending');
        setTimeout(() => {
          if (abortRef.current === controller) startPipeline('generating');
        }, 120);

        // --- Draw mode: generate Excalidraw elements ---
        if (canvasMode === 'draw') {
          const result = await generationApi.generate(
            selectedDiagram.id,
            {
              prompt: request.prompt,
              workspaceId,
              mode: 'excalidraw',
              imageBase64: request.image?.base64,
              imageMediaType: request.image?.mediaType,
            },
            controller.signal,
          );

          startPipeline('validating');

          const skeletons = result.elements || [];
          const { convertToExcalidrawElements } = await import('@excalidraw/excalidraw');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const elements = convertToExcalidrawElements(skeletons as any);

          startPipeline('rendering');

          const data: ExcalidrawData = {
            elements: elements as unknown[],
            appState: {},
            files: {},
          };
          handlers.onExcalidrawGenerated(data);

          const userText = request.image ? `[image] ${request.image.name}\n${request.prompt}` : request.prompt;
          handlers.onAppendChat(selectedDiagram.id, 'user', userText);
          const elemCount = (elements as unknown[]).length;
          handlers.onAppendChat(
            selectedDiagram.id,
            'assistant',
            `Excalidraw diagram updated.\n---steps---\nGenerated ${elemCount} elements from prompt\nConverted skeletons to Excalidraw format\nRendered on canvas`,
          );

          handlers.onStatusChange('Diagram updated');
          handlers.onPipelineChange('completed');
          return undefined;
        }

        // --- Mermaid mode ---
        if (mode === 'fix-only') {
          // Auto-fix: use dedicated endpoint
          const validation = await renderMermaidToSvg(selectedDiagram.mermaidCode, selectedDiagram.theme);
          if (!validation.error) return selectedDiagram.mermaidCode;

          const result = await generationApi.autofix(
            selectedDiagram.id,
            { error: validation.error.raw, workspaceId },
            controller.signal,
          );

          startPipeline('validating');
          const fixValidation = await renderMermaidToSvg(result.code, selectedDiagram.theme);
          if (fixValidation.error) throw new Error(`Mermaid still invalid: ${fixValidation.error.raw}`);

          startPipeline('rendering');

          handlers.onCommitCode(selectedDiagram.id, result.code, 'autofix', 'Auto-fixed Mermaid syntax');
          handlers.onAppendChat(
            selectedDiagram.id,
            'assistant',
            `Auto-fix applied.\n---steps---\nAnalyzed syntax errors in current code\nGenerated corrected Mermaid\nValidated and rendered successfully`,
          );

          handlers.onStatusChange('Diagram updated');
          handlers.onPipelineChange('completed');
          return result.code;
        }

        // --- Mermaid generate ---
        const result = await generationApi.generate(
          selectedDiagram.id,
          {
            prompt: request.prompt,
            workspaceId,
            imageBase64: request.image?.base64,
            imageMediaType: request.image?.mediaType,
          },
          controller.signal,
        );

        let code = result.code || '';

        startPipeline('validating');
        let validation = await renderMermaidToSvg(code, selectedDiagram.theme);

        // Auto-fix if first attempt has errors
        if (validation.error) {
          const fixResult = await generationApi.autofix(
            selectedDiagram.id,
            { error: validation.error.raw, workspaceId },
            controller.signal,
          );
          code = fixResult.code;
          validation = await renderMermaidToSvg(code, selectedDiagram.theme);
        }

        if (validation.error) throw new Error(`Mermaid invalid: ${validation.error.raw}`);

        startPipeline('rendering');

        const firstLine = code.split('\n')[0]?.trim().toLowerCase() || '';
        const diagramType = firstLine.replace(/\s.*$/, '') || 'diagram';
        const hadExistingCode = Boolean(request.codeAtSubmit.trim());

        handlers.onCommitCode(selectedDiagram.id, code, 'chat', 'Generated from prompt');
        const userText = request.image ? `[image] ${request.image.name}\n${request.prompt}` : request.prompt;
        handlers.onAppendChat(selectedDiagram.id, 'user', userText);

        const steps: string[] = [];
        if (request.image) steps.push('Analyzed attached image');
        steps.push(hadExistingCode ? `Updated existing ${diagramType}` : `Created new ${diagramType}`);
        steps.push('Validated syntax and rendered on canvas');

        handlers.onAppendChat(
          selectedDiagram.id,
          'assistant',
          `Diagram updated.\n---steps---\n${steps.join('\n')}`,
        );

        handlers.onStatusChange('Diagram updated');
        handlers.onPipelineChange('completed');
        return code;
      } catch (error) {
        const aborted = error instanceof DOMException && error.name === 'AbortError';
        if (aborted) {
          handlers.onPipelineChange('cancelled');
          handlers.onStatusChange('Generation cancelled');
        } else {
          handlers.onPipelineChange('error');
          handlers.onStatusChange('Request failed');
          if (selectedDiagram) {
            const message = error instanceof Error ? error.message : String(error);
            handlers.onAppendChat(selectedDiagram.id, 'error', message);
          }
        }
        return undefined;
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setIsSending(false);
      }
    },
    [workspaceId, canvasMode, selectedDiagram, handlers, startPipeline],
  );

  const sendPrompt = useCallback(
    async (promptText: string, pendingImage: PendingImage | null, currentExcalidrawElements?: unknown[]) => {
      if (!selectedDiagram) return undefined;
      const text = promptText.trim();
      if (!text && !pendingImage) return undefined;

      const defaultPrompt = canvasMode === 'draw'
        ? 'Convert this attached image into an Excalidraw diagram.'
        : 'Convert this attached image into Mermaid syntax.';

      const request: LastRequest = {
        prompt: text || defaultPrompt,
        image: pendingImage ? { ...pendingImage } : null,
        codeAtSubmit: selectedDiagram.mermaidCode,
        excalidrawElements: currentExcalidrawElements,
      };

      setLastRequest(request);
      return runGeneration(request, 'generate');
    },
    [selectedDiagram, canvasMode, runGeneration],
  );

  const retryLast = useCallback(async () => {
    if (!lastRequest || isSending) return;
    await runGeneration(lastRequest, 'generate');
  }, [lastRequest, isSending, runGeneration]);

  const autoFix = useCallback(async () => {
    if (!selectedDiagram || isSending || !selectedDiagram.mermaidCode.trim()) return;
    const request: LastRequest = {
      prompt: 'Repair the current Mermaid syntax and return corrected Mermaid only.',
      image: null,
      codeAtSubmit: selectedDiagram.mermaidCode,
    };
    await runGeneration(request, 'fix-only');
  }, [selectedDiagram, isSending, runGeneration]);

  return { isSending, lastRequest, stopRequest, sendPrompt, retryLast, autoFix };
}
